import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { subscribeParticipationsActiveByUser } from '@adornis/buchungsheld-db/api/participation.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { LecturerData, subscribeLecturerDataByLecturerID } from '@adornis/buchungsheld-db/entities/lecturerData.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Permission } from '@adornis/buchungsheld-db/enums/roles.js';
import { getPluginForScope } from '@adornis/buchungsheld-db/plugins/plugin.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import '@adornis/buchungsheld-theme/components/tr-checkbox.js';
import '@adornis/buchungsheld-theme/components/tr-loader.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/cms/client/generated-form.js';
import { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { combineLatest, filter, map, of, switchMap } from 'rxjs';
import '../../components/bh-lecturer-data-form';
import { BHLecturerDataForm } from '../../components/bh-lecturer-data-form.js';

@customElement('bh-page-profile-info')
export class BHProfileInfo extends ChemistryLitElement {
  @state() readonly _translation = new TranslationController<BHTranslationDictionary>(this);

  @state() private readonly _user = new RXController(this, currentUser<User>());
  @state() private readonly _participations = new RXController(
    this,
    this._user.observable.pipe(
      filter(Boolean),
      switchMap(user =>
        subscribeParticipationsActiveByUser(user._id)({
          _id: 1,
          _class: 1,
          productID: 1,
          userID: 1,
          product: {
            _id: 1,
            _class: 1,
            name: 1,
            institutionID: 1,
            appointments: { start: 1, end: 1, name: 1, location: 1 },
            slotConfig: { start: 1, end: 1 },
            settings: { replyToMailAddress: 1 },
            beraterAvailabilities: { start: 1, end: 1 },
          },
        }),
      ),
      // order by start date
      map(participations => {
        return participations.toSorted((a, b) => {
          const a_start = a.product.appointments?.at(0)?.start || a.product.slotConfig?.start;
          const b_start = b.product.appointments?.at(0)?.start || b.product.slotConfig?.start;
          console.log({ a_start, b_start });
          if (!a_start && !b_start) return 0;
          if (!a_start) return -1;
          if (!b_start) return 1;
          return a_start.toMillis() - b_start.toMillis();
        });
      }),
    ),
  );

  private readonly _userInstitutions = new RXController(
    this,
    combineLatest([this._user.observable, this._participations.observable]).pipe(
      filter(Boolean),
      switchMap(([user, participations]) => {
        if (!user) return of([]);
        const institutionPromises = Array.from(
          new Set(participations?.map(p => p.product?.institutionID).filter(Boolean)),
        ).map(id =>
          getByID(
            Institution,
            id as string,
          )({
            _id: 1,
            whatsappEnabled: 1,
            name: 1,
          }),
        );
        return Promise.all(institutionPromises);
      }),
    ),
  );

  private readonly _userInstitutionsStamdaten = combineLatest([
    this._user.observable,
    this._userInstitutions.observable,
  ]).pipe(
    switchMap(([user, institutions]) => {
      if (!user) return [];

      const stammdatenPromises = (institutions ?? []).filter(isDefined).map(institution =>
        UserStammdaten.getByUserAndInstitution(
          user._id,
          institution._id,
        )(UserStammdaten.allFields).then(stammdaten => {
          // filter > 4 because _class, _id, instituitionID, userID are always present
          return Object.values((stammdaten as any)?.doc ?? {}).length > 4 ? { institution, stammdaten } : null;
        }),
      );
      return Promise.all(stammdatenPromises).then(data => data.filter(isDefined));
    }),
  );

  private readonly _formController = new GenericFieldFormController(this, this._user.observable, {
    error: error =>
      XDialog.alert(`${this._translation.translate('PROFILE', 'ERROR_SAVING_PROFILE')}: ${error.message}`),
    success: async user => {
      try {
        await user.save();
        await XSnackbar.show(this._translation.translate('PROFILE', 'SUCCESS_SAVING_PROFILE'));
      } catch (e) {
        await XDialog.alert(
          `${this._translation.translate('PROFILE', 'ERROR_SAVING_PROFILE')}: ${(e as Error).message}`,
        );
      }
    },
    disabledFields: { email: 1 },
  });

  @state() private readonly _stammdatenControllers = new RXController(
    this,
    this._userInstitutionsStamdaten.pipe(
      map(data => {
        return data.map(({ institution, stammdaten }) => ({
          institution,
          stammdatenController: new GenericFieldFormController(this, stammdaten, {
            success: async stammdaten => {
              await XDialog.waitFor(stammdaten.save());
              void XSnackbar.show(this._translation.translate('PROFILE', 'SUCCESS_SAVING_PROFILE'));
              this.requestUpdate();
            },
          }),
        }));
      }),
    ),
  );

  @state() private readonly _lecturerData = new RXController(
    this,
    this._user.observable.pipe(
      filter(Boolean),
      switchMap(user => subscribeLecturerDataByLecturerID(user._id)(selectionSet(() => LecturerData, 3))),
    ),
  );

  override render() {
    return html`
      <x-flex space="lg">
        ${this._renderBaseDataSection()}

        <x-hr></x-hr>

        <!-- Lecturer Data-->
        ${this._renderLecturerDataSection()} ${this._renderEditProfileNotificationsSection()}

        <x-hr></x-hr>

        ${this._renderChangePasswordSection()}
      </x-flex>
    `;
  }

  private _lecturerDataFormRef = createRef<BHLecturerDataForm>();
  private _renderLecturerDataSection() {
    //Don't render anything if user doesn't have permission
    return !this._user.value?.hasPermission(Permission.VIEW_LECTURER_WORKSHOPS)
      ? null
      : html`
          <bh-lecturer-data-form
            ${ref(this._lecturerDataFormRef)}
            lecturer-id=${this._user.value._id}
            .lecturerData=${this._lecturerData.value}
          ></bh-lecturer-data-form>
          <x-flex horizontal end>
            <x-button @click=${() => this._lecturerDataFormRef.value?.lecturerDataController.validate()}>
              ${this._translation.translate('SAVE')}
            </x-button>
          </x-flex>
          <x-hr></x-hr>
        `;
  }
  private _renderBaseDataSection() {
    return html`
      <x-flex space="lg">
        <x-flex space="md">
          <generated-form
            placeholder-mode="static-floating"
            .formController=${this._formController}
            .whitelist=${['email', 'firstName', 'lastName']}
            disabled
          ></generated-form>
          <x-flex horizontal end>
            <x-button @click=${() => this._formController.validate({ validateOmitted: false })}>
              ${this._translation.translate('SAVE')}
            </x-button>
          </x-flex>

          <x-flex space="lg">
            ${this._stammdatenControllers.value?.map(({ institution, stammdatenController }) => {
              if (!this._user.value) return html`<x-loading-state></x-loading-state>`;
              return html`
                <x-h4>${institution.name}</x-h4>
                ${getPluginForScope('registration::user-data', institution._id).render(
                  stammdatenController,
                  this._translation,
                  this._user.value,
                )}
                <x-flex horizontal end>
                  <x-button @click=${() => stammdatenController.validate()}>
                    ${this._translation.translate('SAVE')}
                  </x-button>
                </x-flex>
              `;
            })}
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  private _renderEditProfileNotificationsSection() {
    return html`
      <x-flex space="md">
        <x-h3> ${this._translation.translate('PROFILE', 'NOTIFICATIONS', 'header')}: </x-h3>

        <tr-flex horizontal space="md" wrap flex>
          <tr-card space="md" flex>
            <!-- whatsapp -->
            ${(() => {
              const whatsappEnabled = this._userInstitutions.value?.some(institution => !!institution?.whatsappEnabled);
              if (!whatsappEnabled) return nothing;
              return html`
                <tr-flex center>
                  <tr-text bold> ${this._translation.translate('PROFILE', 'NOTIFICATIONS', 'whatsapp')}: </tr-text>
                </tr-flex>

                <tr-flex space="xxs">
                  <tr-flex horizontal space="xs" padding="xxs">
                    <tr-text ${css({ maxWidth: this.sizes.maxReadablWidth })}>
                      ${this._translation.translate('PROFILE', 'NOTIFICATIONS', 'whatsapp_declaration')}
                    </tr-text>
                  </tr-flex>

                  <tr-flex
                    horizontal
                    space="sm"
                    padding="xs"
                    crossaxis-center
                    ${acss({ textWrap: 'nowrap', height: `CALC( ${this.spacing.xl} + ${this.spacing.md} )` })}
                  >
                    <tr-checkbox
                      label="${this._translation.translate('PROFILE', 'NOTIFICATIONS', 'checkboxLabel')}"
                      ${this._formController.field('whatsappNotification')}
                      @value-picked=${() => {
                        this.requestUpdate();
                      }}
                    ></tr-checkbox>

                    ${this._formController.document?.whatsappNotification
                      ? html`
                          <tr-flex horizontal space="sm" crossaxis-center>
                            <tr-text
                              >${this._translation.translate('PROFILE', 'NOTIFICATIONS', 'whichNumberHeader')}</tr-text
                            >
                            <tr-tel-input
                              placeholderMode="inline-floating"
                              placeholder="${this._translation.translate(
                                'PROFILE',
                                'NOTIFICATIONS',
                                'numberPlaceholder',
                              )}"
                              ${this._formController.field('whatsappPhone')}
                            ></tr-tel-input>
                          </tr-flex>
                        `
                      : nothing}
                  </tr-flex>
                </tr-flex>
              `;
            })()}

            <!-- Newsletter -->
            <tr-flex center>
              <tr-text bold> ${this._translation.translate('PROFILE', 'NOTIFICATIONS', 'newsletter')}: </tr-text>
            </tr-flex>

            <tr-flex space="xxs">
              <tr-flex horizontal space="xs" padding="xxs">
                <tr-text ${css({ maxWidth: this.sizes.maxReadablWidth })}>
                  ${this._translation.translate('PROFILE', 'NOTIFICATIONS', 'get_newsletter_mails')}
                </tr-text>
              </tr-flex>

              <tr-flex
                horizontal
                space="sm"
                padding="xs"
                crossaxis-center
                ${acss({ textWrap: 'nowrap', height: `CALC( ${this.spacing.xl} + ${this.spacing.md} )` })}
              >
                <tr-checkbox
                  label="${this._translation.translate('PROFILE', 'NOTIFICATIONS', 'checkboxLabel')}"
                  ${this._formController.field('receivesNewsletter')}
                  @value-picked=${() => {
                    this.requestUpdate();
                  }}
                ></tr-checkbox>
              </tr-flex>
            </tr-flex>
          </tr-card>
        </tr-flex>

        <x-flex space="lg">
          <x-flex horizontal end>
            <x-button @click=${() => this._formController.validate({ validateOmitted: false })}>
              ${this._translation.translate('SAVE')}
            </x-button>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  private _renderChangePasswordSection() {
    let password = '';
    let passwordRepeat = '';
    return html` <x-flex space="md">
      <x-h3> ${this._translation.translate('PROFILE', 'CHANGE_PASSWORD')} </x-h3>
      <x-flex space="sm">
        <x-input
          type="password"
          placeholder="${this._translation.translate('PROFILE', 'NEW_PASSWORD')}"
          @value-picked=${(e: ValueEvent<string>) => {
            password = e.detail.value;
          }}
        ></x-input>
        <x-input
          type="password"
          placeholder=${this._translation.translate('PROFILE', 'REPEAT_NEW_PASSWORD')}
          @value-picked=${(e: ValueEvent<string>) => {
            passwordRepeat = e.detail.value;
          }}
        ></x-input>
      </x-flex>
      <x-flex horizontal end>
        <x-button
          @click=${async () => {
            if (!this._user.value) return;
            if (password !== passwordRepeat)
              return XDialog.alert(this._translation.translate('PROFILE', 'PASSWORDS_DONT_MATCH'));
            try {
              await User.changePassword(this._user.value._id, password);
              await XSnackbar.show(this._translation.translate('PROFILE', 'PASSWORD_CHANGED_SUCCESSFULLY'));
            } catch (e) {
              if (((e as Error).message as string | undefined)?.includes('must contain'))
                return XDialog.alert(this._translation.translate('PROFILE', 'PASSWORD_CHANGED_ERROR_REQUIREMENTS'));
              return XDialog.alert(
                `${this._translation.translate('PROFILE', 'PASSWORD_CHANGED_ERROR')}: ${(e as Error).message}`,
              );
            }
          }}
        >
          ${this._translation.translate('CHANGE_PASSWORD')}
        </x-button>
      </x-flex></x-flex
    >`;
  }
}
